

import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  notification,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  Upload,
  Divider
} from "antd";
import axios from "axios";
import { 
  UserOutlined, 
  PhoneOutlined, 
  CalendarOutlined, 
  CheckCircleOutlined, 
  UploadOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  BookOutlined,
  TrophyOutlined,
  IdcardOutlined,
  TeamOutlined,
  ContactsOutlined,
  FileTextOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { baseUrl } from "../../config";

const { Title, Text } = Typography;
const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

function CompExamResultsForm({ userId, onRefreshData = () => {} }) {
  const [form] = Form.useForm();
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [dobDate, setDobDate] = useState(null);
  const [joiningDate, setJoiningDate] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId) {
      setIsUpdateMode(true);
      fetchCompExamResultDetails();
    }
  }, [userId]);

  const fetchCompExamResultDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/comp_exam_results/read/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        const data = response.data.result;
        
        // Set all form fields
        form.setFieldsValue({
          student_name: data.student_name || '',
          father_name: data.father_name || '',
          gender: data.gender || '',
          village: data.village || '',
          tehsil: data.tehsil || '',
          district: data.district || '',
          mobile_no_1: data.mobile_no_1 || '',
          mobile_no_2: data.mobile_no_2 || '',
          selected_post: data.selected_post || '',
          present_post: data.present_post || '',
          present_address: data.present_address || '',
          preparation_category: data.preparation_category || '',
          duration_months: data.duration_months || null,
          duration_years: data.duration_years || null,
          batch: data.batch || '',
          other_details: data.other_details || '',
          remarks: data.remarks || ''
        });
        
        // Handle dates
        if (data.dob && data.dob !== '0000-00-00' && moment(data.dob).isValid()) {
          setDobDate(data.dob);
        } else {
          setDobDate(null);
        }
        
        if (data.joining_date && data.joining_date !== '0000-00-00' && moment(data.joining_date).isValid()) {
          setJoiningDate(data.joining_date);
        } else {
          setJoiningDate(null);
        }
         
        // Handle image
        if (data.student_photo) {
          setImageUrl(data.student_photo);
          setFileList([
            {
              uid: '-1',
              name: 'Student Photo',
              status: 'done',
              url: data.student_photo,
            },
          ]);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch Competition Exam Result details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      
      console.log('Form values on submit:', values);
      console.log('DOB Date state:', dobDate);
      console.log('Joining Date state:', joiningDate);
      
      // Create FormData
      const formData = new FormData();

      // ✅ Get all form values and add them to FormData
      const allFormValues = form.getFieldsValue();
      console.log('All form values from getFieldsValue:', allFormValues);
      
      // Define all fields
      const textFields = [
        'student_name', 'father_name', 'gender', 'village', 'tehsil', 'district',
        'mobile_no_1', 'mobile_no_2', 'selected_post', 'present_post', 
        'present_address', 'preparation_category', 'batch', 'other_details', 'remarks'
      ];

      // Add all text fields
      textFields.forEach(field => {
        const value = allFormValues[field];
        if (value !== undefined && value !== null) {
          formData.append(field, String(value));
        } else {
          formData.append(field, '');
        }
      });

      // Handle numeric fields
      const durationMonths = allFormValues.duration_months;
      const durationYears = allFormValues.duration_years;
      
      formData.append('duration_months', durationMonths !== undefined && durationMonths !== null ? String(durationMonths) : '0');
      formData.append('duration_years', durationYears !== undefined && durationYears !== null ? String(durationYears) : '0');

      // Handle dates
      if (dobDate && dobDate !== '0000-00-00' && moment(dobDate).isValid()) {
        const formattedDate = moment(dobDate).format('YYYY-MM-DD');
        formData.append('dob', formattedDate);
        console.log('Adding DOB to FormData:', formattedDate);
      } else {
        formData.append('dob', '');
        console.log('Adding empty DOB to FormData');
      }

      if (joiningDate && joiningDate !== '0000-00-00' && moment(joiningDate).isValid()) {
        const formattedJoiningDate = moment(joiningDate).format('YYYY-MM-DD');
        formData.append('joining_date', formattedJoiningDate);
        console.log('Adding joining_date to FormData:', formattedJoiningDate);
      } else {
        formData.append('joining_date', '');
        console.log('Adding empty joining_date to FormData');
      }

      // Handle image upload
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('image', fileList[0].originFileObj);
        console.log('Adding image to FormData');
      }

      // Debug: Log FormData contents
      console.log('Final FormData contents:');
      for (let pair of formData.entries()) {
        if (pair[0] !== 'image') {
          console.log(`${pair[0]}: ${pair[1]}`);
        } else {
          console.log('image: [File Object]');
        }
      }

      // Make API call
      let response;
      if (isUpdateMode) {
        response = await axios.patch(`${baseUrl}/comp_exam_results/update/${userId}`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'multipart/form-data'
          },
        });
      } else {
        response = await axios.post(`${baseUrl}/comp_exam_results/create`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'multipart/form-data'
          },
        });
      }

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode ? 'Competition Exam Result updated successfully!' : 'Competition Exam Result added successfully!',
          placement: 'topRight'
        });
        
        onRefreshData();
        
        if (!isUpdateMode) {
          form.resetFields();
          setDobDate(null);
          setJoiningDate(null);
          setFileList([]);
          setImageUrl(null);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Operation failed',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDobChange = (date) => {
    console.log('DOB changed:', date);
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setDobDate(formattedDate);
      console.log('DOB state set to:', formattedDate);
    } else {
      setDobDate(null);
      console.log('DOB state set to null');
    }
  };

  const handleJoiningDateChange = (date) => {
    console.log('Joining date changed:', date);
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setJoiningDate(formattedDate);
      console.log('Joining date state set to:', formattedDate);
    } else {
      setJoiningDate(null);
      console.log('Joining date state set to null');
    }
  };

  const handleImageChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
    if (!isJpgOrPng) {
      notification.error({
        message: 'Error',
        description: 'You can only upload JPG/PNG files!',
        placement: 'topRight'
      });
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      notification.error({
        message: 'Error',
        description: 'Image must be smaller than 2MB!',
        placement: 'topRight'
      });
      return false;
    }
    return false;
  };

  return (
    <div>
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
        >
          {/* Personal Information Section */}
          <div style={{ marginBottom: '30px' }}>
            <Title level={4} style={{ color: '#7a0000', marginBottom: '20px' }}>
              <IdcardOutlined /> Personal Information
            </Title>
            
            <Row gutter={[24, 16]}>
              {/* Photo Upload */}
              <Col xs={24} lg={6}>
                <div style={{ textAlign: 'center' }}>
                  <Item label={<Text strong>Student Photo</Text>} name="studentPhoto">
                    <div style={{
                      border: '2px dashed #d9d9d9',
                      borderRadius: '12px',
                      padding: '20px',
                      background: '#fafafa',
                      minHeight: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Upload
                        name="image"
                        listType="picture"
                        accept="image/*"
                        beforeUpload={beforeUpload}
                        onChange={handleImageChange}
                        fileList={fileList}
                        maxCount={1}
                        showUploadList={{
                          showPreviewIcon: false,
                          showRemoveIcon: true,
                          showDownloadIcon: false
                        }}
                        style={{ width: '100%' }}
                      >
                        {fileList.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <UploadOutlined style={{ 
                              fontSize: '48px', 
                              color: '#7a0000',
                              marginBottom: '16px',
                              display: 'block'
                            }} />
                            <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                              Click to Upload Photo
                            </div>
                            <div style={{ fontSize: '12px', color: '#999' }}>
                              JPG, PNG files up to 2MB
                            </div>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '10px' }}>
                            <div style={{ fontSize: '14px', color: '#52c41a', marginTop: '10px' }}>
                              ✓ Photo uploaded successfully
                            </div>
                          </div>
                        )}
                      </Upload>
                    </div>
                  </Item>
                </div>
              </Col>

              <Col xs={24} lg={18}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Item
                      label={<Text strong>Student Name</Text>}
                      name="student_name"
                      rules={[{ required: true, message: 'Please enter student name' }]}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: '#7a0000' }} />}
                        placeholder="Enter full name"
                        size="large"
                        style={{ borderRadius: '8px' }}
                      />
                    </Item>
                  </Col>
                  
                  <Col xs={24} sm={12}>
                    <Item
                      label={<Text strong>Father's Name</Text>}
                      name="father_name"
                      rules={[{ required: true, message: 'Please enter father\'s name' }]}
                    >
                      <Input
                        prefix={<TeamOutlined style={{ color: '#7a0000' }} />}
                        placeholder="Enter father's name"
                        size="large"
                        style={{ borderRadius: '8px' }}
                      />
                    </Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Item
                      label={<Text strong>Gender</Text>}
                      name="gender"
                      rules={[{ required: true, message: 'Please select gender' }]}
                    >
                      <Select
                        placeholder="Select gender"
                        size="large"
                        style={{ borderRadius: '8px' }}
                      >
                        <Option value="Male">Male</Option>
                        <Option value="Female">Female</Option>
                        <Option value="Other">Other</Option>
                        <Option value="Prefer not to say">Prefer not to say</Option>
                      </Select>
                    </Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Item label={<Text strong>Date of Birth</Text>}>
                      <DatePicker
                        onChange={handleDobChange}
                        value={dobDate && dobDate !== "0000-00-00" && moment(dobDate).isValid() ? moment(dobDate) : null}
                        format="DD-MM-YYYY"
                        placeholder="Select date of birth"
                        size="large"
                        style={{ width: '100%', borderRadius: '8px' }}
                        suffixIcon={<CalendarOutlined style={{ color: '#7a0000' }} />}
                        allowClear
                      />
                    </Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Contact & Location Section */}
          <div style={{ marginBottom: '30px' }}>
            <Title level={4} style={{ color: '#7a0000', marginBottom: '20px' }}>
              <ContactsOutlined /> Contact & Location Details
            </Title>
            
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12}>
                <Item
                  label={<Text strong>Mobile Number 1</Text>}
                  name="mobile_no_1"
                  rules={[
                    { required: true, message: 'Please enter mobile number' },
                    { pattern: /^\d{10,12}$/, message: 'Mobile must be 10-12 digits!' }
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined style={{ color: '#7a0000' }} />}
                    placeholder="Primary mobile number"
                    size="large"
                    style={{ borderRadius: '8px' }}
                    maxLength={12}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12}>
                <Item
                  label={<Text strong>Mobile Number 2</Text>}
                  name="mobile_no_2"
                  rules={[{ pattern: /^\d{10,12}$/, message: 'Mobile must be 10-12 digits!' }]}
                >
                  <Input
                    prefix={<PhoneOutlined style={{ color: '#7a0000' }} />}
                    placeholder="Alternative mobile number"
                    size="large"
                    style={{ borderRadius: '8px' }}
                    maxLength={12}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={8}>
                <Item label={<Text strong>Village</Text>} name="village">
                  <Input
                    prefix={<HomeOutlined style={{ color: '#7a0000' }} />}
                    placeholder="Village name"
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={8}>
                <Item label={<Text strong>Tehsil</Text>} name="tehsil">
                  <Input
                    prefix={<EnvironmentOutlined style={{ color: '#7a0000' }} />}
                    placeholder="Tehsil name"
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={8}>
                <Item label={<Text strong>District</Text>} name="district">
                  <Input
                    prefix={<EnvironmentOutlined style={{ color: '#7a0000' }} />}
                    placeholder="District name"
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                </Item>
              </Col>

              <Col span={24}>
                <Item label={<Text strong>Present Address</Text>} name="present_address">
                  <TextArea
                    placeholder="Enter current address"
                    rows={3}
                    style={{ borderRadius: '8px' }}
                  />
                </Item>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Support & Exam Details Section */}
          <div style={{ marginBottom: '30px' }}>
            <Title level={4} style={{ color: '#7a0000', marginBottom: '20px' }}>
              <TrophyOutlined /> Support & Examination Details
            </Title>
            
            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Item
                  label={<Text strong style={{ color: '#52c41a' }}>Support/Help Provided</Text>}
                  name="preparation_category"
                  rules={[{ required: true, message: 'Please select the type of support provided' }]}
                  extra={<Text type="secondary">Select the type of assistance our organization provided to this student</Text>}
                >
                  <Select
                    placeholder="Select the type of support/help provided"
                    size="large"
                    style={{ borderRadius: '8px' }}
                    options={[
                      {
                        value: "Shri Kheteshwar Pratiyogi Pariksha Kendra, Badli",
                        label: "Shri Kheteshwar Pratiyogi Pariksha Kendra, Badli"
                      },
                      {
                        value: "Financial Help - External Preparation",
                        label: "Financial Assistance - External Preparation"
                      },
                      {
                        value: "Coaching Classes Only",
                        label: "📚 Coaching Classes Only"
                      },
                      {
                        value: "Library Facility Only",
                        label: "📖 Library Facility Only"
                      },
                      {
                        value: "Other Support",
                        label: "🤝 Other Support/Assistance"
                      }
                    ]}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12}>
                <Item
                  label={<Text strong>Selected Post</Text>}
                  name="selected_post"
                  rules={[{ required: true, message: 'Please enter selected post' }]}
                >
                  <Input
                    prefix={<TrophyOutlined style={{ color: '#7a0000' }} />}
                    placeholder="Position/Post achieved"
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12}>
                <Item label={<Text strong>Joining Date</Text>}>
                  <DatePicker
                    onChange={handleJoiningDateChange}
                    value={joiningDate && joiningDate !== "0000-00-00" && moment(joiningDate).isValid() ? moment(joiningDate) : null}
                    format="DD-MM-YYYY"
                    placeholder="Select joining date"
                    size="large"
                    style={{ width: '100%', borderRadius: '8px' }}
                    suffixIcon={<CalendarOutlined style={{ color: '#7a0000' }} />}
                    allowClear
                  />
                </Item>
              </Col>

              <Col span={24}>
                <Item label={<Text strong>Present Post</Text>} name="present_post">
                  <Input
                    placeholder="Current position/designation"
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                </Item>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Additional Information Section */}
          <div style={{ marginBottom: '30px' }}>
            <Title level={4} style={{ color: '#7a0000', marginBottom: '20px' }}>
              <ClockCircleOutlined /> Duration & Additional Information
            </Title>
            
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={8}>
                <Item label={<Text strong>Duration (Years)</Text>} name="duration_years">
                  <InputNumber
                    placeholder="Years"
                    size="large"
                    style={{ width: '100%', borderRadius: '8px' }}
                    min={0}
                    max={10}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={8}>
                <Item label={<Text strong>Duration (Months)</Text>} name="duration_months">
                  <InputNumber
                    placeholder="Months"
                    size="large"
                    style={{ width: '100%', borderRadius: '8px' }}
                    min={0}
                    max={120}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={8}>
                <Item label={<Text strong>Batch</Text>} name="batch">
                  <Input
                    prefix={<BookOutlined style={{ color: '#7a0000' }} />}
                    placeholder="e.g., 2023-24"
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                </Item>
              </Col>

              <Col span={24}>
                <Item label={<Text strong>Other Details</Text>} name="other_details">
                  <TextArea
                    placeholder="Enter any other relevant details"
                    rows={4}
                    style={{ borderRadius: '8px' }}
                    maxLength={500}
                    showCount
                  />
                </Item>
              </Col>

              <Col span={24}>
                <Item label={<Text strong>Remarks</Text>} name="remarks">
                  <TextArea
                    placeholder="Enter any remarks or notes"
                    rows={4}
                    style={{ borderRadius: '8px' }}
                    maxLength={500}
                    showCount
                  />
                </Item>
              </Col>
            </Row>
          </div>

          {/* Submit Button */}
          <Divider />
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
            <Button size="large" style={{ borderRadius: 8 }} onClick={() => onRefreshData()}>Cancel</Button>
            <Button type="primary" htmlType="submit" size="large" loading={loading} icon={<CheckCircleOutlined />} style={{ borderRadius: 8, background: '#7a0000', borderColor: '#7a0000', minWidth: 160 }}>
              {isUpdateMode ? 'Update Record' : 'Save Record'}
            </Button>
          </div>
        </Form>
    </div>
  );
}

export default CompExamResultsForm;